import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./AddFamilyMemberModal.module.css";
import { useDispatch, useSelector } from "react-redux";
import familySearch from "../../../store/family/familySearch/FamilySearch";
import Cookies from 'js-cookie';
import { familySearchAction } from "../../../store/family/familySearch/FamilySearchSlice";

const TABS = [
  { key: "phone", icon: "/assets/subscriptions/family/phone.svg", labelKey: "phone", desc: "enter_full_phone" },
  { key: "email", icon: "/assets/subscriptions/family/email.svg", labelKey: "email_short", desc: "enter_full_email" },
  { key: "username", icon: "/assets/subscriptions/family/user.svg", labelKey: "username", desc: "enter_full_username" },
];

const INPUT_TYPES = {
  email: "email",
  username: "text",
  phone: "tel",
};

export default function AddFamilyMemberModal({ onClose, t, loading, setLoading, handleInviteButton }) {
  const dispatch = useDispatch();
  const overlayRef = useRef(null);
  const [activeTab, setActiveTab] = useState("phone");
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [searchResult, setSearchResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const familySearchData = useSelector((state) => state.familySearch);
  const token = Cookies.get('auth_token');

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const validate = useCallback((val, type) => {
    const trimmed = val?.trim();
    if (type === "email") {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) ? "" : t("invalid_email");
    }
    if (type === "phone") {
      return /^\+?[0-9]{7,15}$/.test(trimmed) ? "" : t("invalid_phone_number");
    }
    return "";
  }, [t]);

  useEffect(() => {
    if (familySearchData?.status === "succeeded") {
      setSearching(false);
      setSearchResult("found");
      setFoundUser(familySearchData.data);
      dispatch(familySearchAction.familySearchReset());
    } else if (familySearchData?.status === "failed") {
      setSearching(false);
      setErrorMessage(familySearchData.error.Error);
      setSearchResult("not_found");
      dispatch(familySearchAction.familySearchReset());
    }
  }, [dispatch, familySearchData]);

  const handleSearch = useCallback(() => {
    if (!value.trim()) return;
    const err = validate(value, activeTab);
    if (err) return setError(err)

    if (token) {
      setSearching(true);
      setSearchResult(null);
      setFoundUser(null);
      setErrorMessage(null);
      dispatch(familySearch(token, value.trim()));
    }
  }, [value, activeTab, validate, token, dispatch]);

  useEffect(() => {
    if (activeTab !== 'phone' || !value.trim()) return;
    if (value.startsWith('+374') && value.length === 12) return handleSearch();
    if (value.startsWith('0') && value.length === 9) return handleSearch();
  }, [activeTab, validate, value, handleSearch])

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setValue("");
    setError("");
    setSearchResult(null);
    setErrorMessage(null);
    setFoundUser(null);
  };

  const handleSend = async () => {
    if (!value.trim()) return;
    const err = validate(value, activeTab);
    if (err) return setError(err);
    setLoading("invite");
    handleInviteButton?.(value);
  };

  const handleChange = (e) => {
    setValue(activeTab === 'phone' ? sanitizePhone(e.target.value) : e.target.value);
    setSearchResult(null);
    setFoundUser(null);
    setErrorMessage(null);
    if (error) setError(validate(e.target.value, activeTab));
  };

  const sanitizePhone = (raw) => {
    const digitsAndPlus = raw.replace(/[^\d+]/g, '');
    return digitsAndPlus.replace(/(?!^)\+/g, '');
  };

  return (
    <div className={styles.overlay} ref={overlayRef} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.heroSection}>
          <img src="/assets/icons/vinni.svg" alt="" className={styles.monsterImg}/>
        </div>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <img src="/assets/subscriptions/close.svg" alt="" width={16} height={16} />
        </button>

        <div className={styles.body}>
          <h2 className={styles.title}>{t?.("add_family_member")}</h2>
          <p className={styles.subtitle}>{t?.("share_invite_text")}</p>

          <div className={styles.tabs}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
                onClick={() => handleTabChange(tab.key)}
              >
                <img src={tab.icon} alt="" height={16} width={16}/>
                <span className={styles.tabLabel}>{t?.(tab.labelKey)}</span>
              </button>
            ))}
          </div>

          <label className={styles.inputLabel}>
            {t(TABS.find(tab => tab.key === activeTab)?.desc)}
          </label>

          <div className={styles.inputWrapper}>
            <input
              className={`${styles.input}`}
              type={INPUT_TYPES[activeTab]}
              value={value}
              onChange={handleChange}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData("text").trim();
                setValue(pasted);
              }}
              disabled={searching}
              autoFocus
            />
            <button
              className={styles.searchBtn}
              onClick={handleSearch}
              disabled={searching || !value.trim()}
              aria-label="Search"
            >
              {searching
                ? <span className={styles.inputSpinner} />
                : <img src="/assets/subscriptions/family/search.svg" alt="" width={20} height={20} />
              }
            </button>
          </div>

          {error && <span className={styles.errorMsg}>{error}</span>}
          {!error && searchResult === "found" && foundUser && (
            <div className={styles.foundUser}>
              <div className={styles.foundAvatar}>
                {foundUser.displayName?.[0]?.toUpperCase() ?? foundUser.username?.[0]?.toUpperCase() ?? "-"}
              </div>
              <div className={styles.foundInfo}>
                <span className={styles.foundName}>{foundUser.displayName ?? foundUser.username ?? "-"}</span>
                <span className={styles.foundEmail}>{foundUser.email ?? foundUser.phoneNumber ?? foundUser.username ?? "-"}</span>
              </div>
            </div>
          )}

          {!error && searchResult === "not_found" && (
            <span className={styles.searchNotFound}>{`${t("toast_error")} ${t(errorMessage) ?? errorMessage}`}</span>
          )}

          <button
            className={styles.sendBtn}
            onClick={handleSend}
            disabled={loading === "invite" || !value.trim() || error || searchResult !== "found" || !foundUser}
          >
            {loading === "invite" ? <span className={styles.btnLoader} /> : t?.("send_invite")}
          </button>
          <button className={styles.cancelBtn} onClick={onClose}>{t?.("cancel")}</button>
        </div>
      </div>
    </div>
  );
}